import { glyphHorizontalBounds, glyphVerticalBounds, type SmuflGlyph } from './smufl';

export interface AccidentalInput {
  glyph: SmuflGlyph;
  y: number;
  scale: number;
}

/** Pack one staff's simultaneous accidentals left of its leftmost head.
 * Ink intervals that overlap need separate columns. Widely separated
 * pitches can share a column; identical symbols on a unison are printed once.
 * Coordinates and gaps are staff spaces, independent of display size. */
export function accidentalColumns(inputs: readonly AccidentalInput[], rightEdge: number) {
  const placements = inputs.map(() => ({ x: 0, duplicate: false }));
  const seen = new Map<string, number>();
  const unique: number[] = [];
  inputs.forEach((input, i) => {
    const key = `${input.glyph}:${input.y}:${input.scale}`;
    if (seen.has(key)) placements[i].duplicate = true;
    else {
      seen.set(key, i);
      unique.push(i);
    }
  });
  unique.sort((a, b) => inputs[a].y - inputs[b].y);
  // Highest and lowest notes get first choice of the nearest column, then
  // work inwards. This keeps wide chord stacks compact without overlapping ink.
  const order: number[] = [];
  for (let lo = 0, hi = unique.length - 1; lo <= hi; lo++, hi--) {
    order.push(unique[lo]);
    if (lo !== hi) order.push(unique[hi]);
  }
  const columns: number[][] = [];
  const interval = (i: number) => {
    const input = inputs[i];
    const bounds = glyphVerticalBounds(input.glyph);
    return {
      top: input.y + bounds.top * input.scale,
      bottom: input.y + bounds.bottom * input.scale,
    };
  };
  for (const i of order) {
    const bounds = interval(i);
    const column = columns.find((members) =>
      members.every((j) => {
        const other = interval(j);
        return bounds.top >= other.bottom + 0.2 || other.top >= bounds.bottom + 0.2;
      }),
    );
    if (column) column.push(i);
    else columns.push([i]);
  }
  let right = rightEdge;
  let left = rightEdge;
  for (const column of columns) {
    const width = Math.max(
      ...column.map((i) => {
        const bounds = glyphHorizontalBounds(inputs[i].glyph);
        return (bounds.right - bounds.left) * inputs[i].scale;
      }),
    );
    for (const i of column) {
      placements[i].x = right - glyphHorizontalBounds(inputs[i].glyph).right * inputs[i].scale;
    }
    left = right - width;
    right = left - 0.25;
  }
  return { placements, left };
}
